import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';

/**
 * Phase 9, Crew Email Send.
 *
 * The send itself runs in the send-crew-route Edge Function, because the Resend
 * API key cannot live in the browser. Preview and send are the same call with
 * one flag, so what the office approves is the function's own output rather
 * than a second renderer that drifts from it.
 */

export interface CrewEmailSend {
    created_at: string;
    recipient: string;
    subject: string;
    item_count: number;
    status: 'sent' | 'failed';
    provider_message_id: string | null;
    error: string | null;
    sent_by_email: string | null;
}

export interface RoutePreview {
    success: true;
    preview: true;
    crew: { id: string; name: string; email: string | null };
    subject: string;
    html: string;
    item_count: number;
    previous_sends: CrewEmailSend[];
}

export interface RouteSent {
    success: true;
    crew: { id: string; name: string; email: string | null };
    subject: string;
    item_count: number;
    provider_message_id: string | null;
}

export const crewEmailKeys = {
    crew: (id: string | undefined) => ['crewEmail', id ?? 'none'] as const,
    sends: (id: string | undefined, date: string | undefined) =>
        ['crewEmailSends', id ?? 'none', date ?? 'none'] as const,
};

/** The crew's address on its own, so the route view does not need the whole row. */
export function useCrewEmail(crewId: string | undefined) {
    return useQuery({
        queryKey: crewEmailKeys.crew(crewId),
        enabled: Boolean(crewId),
        queryFn: async (): Promise<string | null> => {
            if (!crewId) return null;
            const { data, error } = await supabase
                .from('crews')
                .select('email')
                .eq('id', crewId)
                .maybeSingle();
            if (error) throw new Error(error.message);
            return data?.email ?? null;
        },
        staleTime: 1000 * 60,
    });
}

/**
 * Admin only, enforced by the crews_admin_all policy rather than by hiding the
 * field. A non-admin editing it gets a policy error rather than a silent no-op.
 */
export function useSetCrewEmail() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ crewId, email }: { crewId: string; email: string | null }) => {
            const { error } = await supabase
                .from('crews')
                .update({ email: email?.trim() || null })
                .eq('id', crewId);
            // The database carries a shape check on this column, so a typed-in
            // crew name comes back as a constraint violation here rather than
            // becoming a send that fails silently hours later.
            if (error) throw new Error(error.message);
        },
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: crewEmailKeys.crew(vars.crewId) });
        },
    });
}

/** What has already gone out for this crew and date. Drives the duplicate warning. */
export function useCrewEmailSends(crewId: string | undefined, isoDate: string | undefined) {
    return useQuery({
        queryKey: crewEmailKeys.sends(crewId, isoDate),
        enabled: Boolean(crewId && isoDate),
        queryFn: async (): Promise<CrewEmailSend[]> => {
            if (!crewId || !isoDate) return [];
            const { data, error } = await supabase
                .from('crew_email_sends')
                .select('created_at, recipient, subject, item_count, status, provider_message_id, error, sent_by_email')
                .eq('crew_id', crewId)
                .eq('route_date', isoDate)
                .order('created_at', { ascending: false });
            if (error) throw new Error(error.message);
            return (data ?? []) as CrewEmailSend[];
        },
        staleTime: 1000 * 15,
    });
}

/**
 * Calls the Edge Function.
 *
 * functions.invoke rather than a hand-built fetch, so the session token is
 * attached for us and no Supabase URL is duplicated into this file. The catch
 * is that a non-2xx response arrives as an error with the body tucked inside
 * error.context, and the function returns its useful messages there: "Admins
 * only", "M&M has no email address on file". Reading it out is what turns a
 * bare "FunctionsHttpError" into something the office can act on.
 */
async function invokeSendCrewRoute<T>(body: Record<string, unknown>): Promise<T> {
    const { data, error } = await supabase.functions.invoke('send-crew-route', { body });
    if (error) {
        let detail = error.message;
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
            try {
                const parsed = await ctx.json();
                if (parsed?.error) detail = String(parsed.error);
            } catch {
                // Body was not JSON. Keep the original message.
            }
        }
        throw new Error(detail);
    }
    if (data && data.success === false) throw new Error(String(data.error ?? 'Send failed'));
    return data as T;
}

export function usePreviewCrewRoute() {
    return useMutation({
        mutationFn: ({ crewId, isoDate }: { crewId: string; isoDate: string }) =>
            invokeSendCrewRoute<RoutePreview>({
                crew_id: crewId,
                route_date: isoDate,
                preview: true,
            }),
    });
}

export function useSendCrewRoute() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ crewId, isoDate }: { crewId: string; isoDate: string }) =>
            invokeSendCrewRoute<RouteSent>({
                crew_id: crewId,
                route_date: isoDate,
                preview: false,
            }),
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: crewEmailKeys.sends(vars.crewId, vars.isoDate) });
        },
    });
}
